import React, { useRef, useState, useEffect } from 'react'
import styles from '../css/ColumnPicker.module.css'

// Columns that are always visible and not user-configurable
const LOCKED_COLS = new Set(['_action', '_status', '_action_reason'])

// Below this viewport height, use a centered modal instead of a dropdown
const MODAL_THRESHOLD = 900

export default function ColumnPicker({ columnDefs, hiddenCols, onToggle, onReset, isCustomized }) {
    const [open, setOpen] = useState(false)
    const [popoverStyle, setPopoverStyle] = useState({})
    const [useModal, setUseModal] = useState(false)
    const wrapRef = useRef(null)
    const triggerRef = useRef(null)

    // Close on outside click (dropdown mode only — modal uses backdrop)
    useEffect(() => {
        if (!open || useModal) return
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
        }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [open, useModal])

    const handleOpen = () => {
        if (!open) {
            const isSmall = window.innerHeight < MODAL_THRESHOLD
            setUseModal(isSmall)
            if (!isSmall && triggerRef.current) {
                const rect = triggerRef.current.getBoundingClientRect()
                const GAP = 6
                const MIN_HEIGHT = 120
                const spaceBelow = window.innerHeight - rect.bottom - GAP
                const spaceAbove = rect.top - GAP
                if (spaceBelow >= MIN_HEIGHT || spaceBelow >= spaceAbove) {
                    setPopoverStyle({ top: `calc(100% + ${GAP}px)`, bottom: 'auto', maxHeight: Math.max(MIN_HEIGHT, spaceBelow - 8) + 'px' })
                } else {
                    setPopoverStyle({ bottom: `calc(100% + ${GAP}px)`, top: 'auto', maxHeight: Math.max(MIN_HEIGHT, spaceAbove - 8) + 'px' })
                }
            }
        }
        setOpen(o => !o)
    }

    const configurableDefs = columnDefs.filter(d => !LOCKED_COLS.has(d.id))
    const hiddenCount = configurableDefs.filter(d => hiddenCols.has(d.id)).length

    const pickerContent = (
        <div className={useModal ? styles.modal : styles.popover} style={useModal ? undefined : popoverStyle}>
            <div className={styles.popoverHeader}>
                <span>Show / hide columns</span>
                <div className={styles.headerActions}>
                    {isCustomized && (
                        <button className={styles.resetBtn} onClick={() => { onReset(); setOpen(false) }}>
                            Reset all
                        </button>
                    )}
                    {useModal && (
                        <button className={styles.closeBtn} onClick={() => setOpen(false)} aria-label="Close">
                            ✕
                        </button>
                    )}
                </div>
            </div>
            <ul className={styles.list}>
                {configurableDefs.map(def => {
                    const hidden = hiddenCols.has(def.id)
                    return (
                        <li key={def.id} className={styles.item}>
                            <label className={styles.label}>
                                <input
                                    type="checkbox"
                                    checked={!hidden}
                                    onChange={() => onToggle(def.id)}
                                    className={styles.checkbox}
                                />
                                {def.title}
                            </label>
                        </li>
                    )
                })}
            </ul>
        </div>
    )

    return (
        <div className={styles.wrap} ref={wrapRef}>
            <button
                ref={triggerRef}
                className={[styles.trigger, hiddenCount > 0 ? styles.triggerActive : ''].filter(Boolean).join(' ')}
                onClick={handleOpen}
                title="Show/hide columns"
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                    <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
                </svg>
                Columns
                {hiddenCount > 0 && <span className={styles.badge}>{hiddenCount} hidden</span>}
            </button>

            {open && useModal && (
                <div className={styles.backdrop} onMouseDown={() => setOpen(false)}>
                    <div onMouseDown={e => e.stopPropagation()}>
                        {pickerContent}
                    </div>
                </div>
            )}
            {open && !useModal && pickerContent}
        </div>
    )
}
